// components/modals/ForgotPasswordModal.jsx
import React, { useRef, useEffect, useState } from "react";
import { Modal, Input, Button } from "antd";
import { useLanguage } from "../../context/LanguageContext";

export default function ForgotPasswordModal({
  open,
  onClose,
  step,
  email,
  setEmail,
  code,
  setCode,
  newPassword,
  setNewPassword,
  loading,
  onSendResetCode,
  onResetPassword,
  onResendCode,
}) {
  const codeRefs = useRef([]);
  const codeArray = code.split("").slice(0, 6);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const { t } = useLanguage();

  useEffect(() => {
    if (open && step === 2) {
      setTimeout(() => {
        codeRefs.current[0]?.focus();
      }, 100);
    }
  }, [open, step]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (!open) {
      setResendCooldown(0);
      setResendLoading(false);
    }
  }, [open]);

  const handleCodeChange = (value, index) => {
    const codeChars = code.split("");
    codeChars[index] = value.slice(-1);
    const newCode = codeChars.join("").padEnd(6, "");
    setCode(newCode);
    if (value && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const codeChars = code.split("");
      codeChars[index - 1] = "";
      setCode(codeChars.join(""));
      setTimeout(() => codeRefs.current[index - 1]?.focus(), 60);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").trim().slice(0, 6);
    if (/^\d+$/.test(paste)) {
      setCode(paste.padEnd(6, ""));
      setTimeout(() => codeRefs.current[paste.length - 1]?.focus(), 60);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await onResendCode();
      setResendCooldown(60);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t("auth.forgotPassword")}
      centered
      destroyOnClose
    >
      <div className="space-y-4">
        {step === 1 ? (
          <>
            <div className="text-gray-600 mb-4">
              {t("auth.enterEmailToReceiveCode")}
            </div>
            <Input
              type="email"
              placeholder={t("auth.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              loading={loading}
              onClick={onSendResetCode}
              className={`w-full ${
                !email.trim()
                  ? "bg-gray-200"
                  : "bg-primary text-white hover:bg-textPrimary"
              }`}
              disabled={!email.trim()}
            >
              {loading ? t("auth.sending") : t("auth.sendResetCode")}
            </Button>
          </>
        ) : (
          <>
            <div className="text-gray-600 mb-4">
              {t("auth.checkEmailEnterCode")}
            </div>
            <div className="flex gap-2 justify-center mb-4" onPaste={handlePaste}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Input
                  key={index}
                  ref={(el) => (codeRefs.current[index] = el)}
                  maxLength={1}
                  value={codeArray[index] || ""}
                  onChange={(e) => handleCodeChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-10 text-center"
                  size="large"
                />
              ))}
            </div>

            {/* Resend Code Section */}
            <div className="flex justify-center mb-4">
              <Button
                onClick={handleResendCode}
                loading={resendLoading}
                disabled={resendCooldown > 0 || resendLoading}
                className="p-0 text-primary hover:text-textPrimary"
              >
                {resendCooldown > 0
                  ? `${t("auth.resendCodeIn")} ${resendCooldown}s`
                  : resendLoading
                  ? t("auth.sending")
                  : t("auth.resendCode")}
              </Button>
            </div>

            <Input.Password
              placeholder={t("auth.passwordPlaceholder")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Button
              loading={loading}
              onClick={onResetPassword}
              className={`w-full ${
                !code.trim()
                  ? "bg-gray-200"
                  : "bg-primary text-white hover:bg-textPrimary"
              }`}
              disabled={!code.trim()}
            >
              {loading ? t("auth.resetting") : t("auth.resetPassword")}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
